<template>
        <v-dialog
            v-model="is_visible"
            max-width="600px"
            @click:outside.stop="cancelChanges"
            @keydown="handleKeyDown"
            :retain-focus="false"
            ref="vdialog"
            :scrollable="false"
        >
            <v-card>
                <v-card-title class="bg-gray-800 py-2 px-6 text-white">{{ title }}</v-card-title>
                <v-divider></v-divider>
                <v-card-subtitle v-show="enable_search" class="pb-0">
                    <br/>
                    <v-text-field class="mb-0 pb-0"
                        outlined
                        v-model="search"
                        label="Search"
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        dense
                        ref="searchField"
                        />
                </v-card-subtitle>
                <v-card-text>
                    <v-form>
                        <smart-table
                            v-model="selected"
                            :headers="headers"
                            :items="records_to_show"
                            :item-class= "rowClasses"
                            :items-per-page="1000"
                            :search="search"
                            :item-key="item_key"
                            :loading="loading"
                            class="flat"
                            :multi-sort="true"
                            :footer-props="{showFirstLastPage: true}"
                            :hide-default-footer="true"
                            show-select
                            :single-select="!multiple"
                            fixed-header
                            :height="`${table_height}px`"
                            ref="datatable"
                        >
                        </smart-table>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="scrollToTop" dense text v-show="scrollbar_is_present"><v-icon>mdi-arrow-collapse-up</v-icon></v-btn>
                    <v-btn @click="scrollToBottom" dense text v-show="scrollbar_is_present"><v-icon>mdi-arrow-collapse-down</v-icon></v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        @click="cancelChanges"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        text
                        @click="saveChanges"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>

        </v-dialog>
</template>

<script>
    import SmartTable from "@/Generic/SmartTable";

    export default {
        name: "RecordPickerDialog",
        components: {
            SmartTable,
        },
        props: {
            'value': {
                // An array of the currently selected records -- this is what will get v-modeled
                type: Array,
                required: false,
                default: function () {
                    return []
                },
            },
            'show': {
                // Indicates if the dialog is currently showing
                type: Boolean,
                required: true,
                default: function () {
                    return undefined
                },
            },
            'input': {
                // The data that will be used to populate the list of records in the picker window
                type: Array,
                required: true,
                default: function () {
                    return undefined
                },
            },
            'headers': {
                // An array of headers defines the columns used for the datatable in the picker window.  Same format as expected for v-data-table
                type: Array,
                required: true,
                default: function () {
                    return []
                },
            },
            'item_key': {
                // The name of the unique column/field from the input
                type: String,
                required: false,
                default: function () {
                    return 'id'
                },
            },
            'alphabetic_key': {
                // The name of the column that can be used for alphabetic sorting
                type: String,
                required: false,
                default: function () {
                    return undefined
                },
            },
            'multiple': {
                // Determines whether the user can pick multiple records from the picker window
                type: Boolean,
                required: false,
                default: function () {
                    return false
                },
            },
            'title': {
                // The title for the dialog window
                type: String,
                required: false,
                default: function () {
                    return null
                },
            },
            'enable_search': {
                // Determines whether the user can pick multiple records from the picker window
                type: Boolean,
                required: false,
                default: function () {
                    return false
                },
            },
        },

        data() {
            return {
                selected: [], // A local copy of the v-modeled value
                records_to_show: [], // A local copy of the input
                scrollbar_is_present: false,
                search: null,
                loading: false,
            }
        },
        async created() {

        },
        async mounted() {
            this.$nextTick(()=>{
                this.checkForScrollbar()
            })
        },
        computed: {
            is_visible: {
                get () {
                    return this.show
                },
                set (value) {
                    this.$emit('update:show', value)
                }
            },
            table_height: function (){
                let other_ui_height = 350
                let heights_to_consider = [
                    this.$vuetify.breakpoint.height - other_ui_height,
                    window.innerHeight - other_ui_height,
                ]
                let smallest_height = Math.min(...heights_to_consider) // Use the smallest value from the heights being considered
                return smallest_height || 400
            },
            selected_ids_only: function() {
                return this.selected?.reduce((prev,curr)=>{
                    let this_item_key = this.item_key
                    let this_value = curr[this_item_key]
                    prev.push(this_value)
                    return prev
                },[])
            },
        },
        methods: {
            signalAllDone() {
                this.$emit('closeDialog')
            },
            rowClasses(item) {
                let class_list = []
                if (this.item_key && item.hasOwnProperty(this.item_key) && this.selected?.includes(item[this.item_key])) class_list.push('selected-record')
                return class_list.join(' ')
            },
            handleKeyDown(keyboardEvent) {
                if (keyboardEvent.keyCode === 27) {  // Escape key was pressed
                    this.cancelChanges()
                }
            },
            saveChanges() {
                this.$emit('input', this.selected)
                this.signalAllDone()
            },
            cancelChanges() {
                this.selected = this.value
                this.signalAllDone()
            },
            prep_data_for_ui: function() {
                return this.input.sort((a,b) => {
                    let is_selected_a = this.selected_ids_only?.includes(a[this.item_key])
                    let is_selected_b = this.selected_ids_only?.includes(b[this.item_key])
                    let both_have_same_selection_status = (is_selected_a && is_selected_b) || (!is_selected_a && !is_selected_b)
                    let name_a = this.alphabetic_key ? a[this.alphabetic_key].toUpperCase() : null
                    let name_b = this.alphabetic_key ? b[this.alphabetic_key].toUpperCase() : null

                    if (both_have_same_selection_status) {
                        if (this.alphabetic_key && (name_a < name_b)) return -1
                        if (this.alphabetic_key && (name_a > name_b)) return 1
                        return 0
                    } else if (is_selected_a) {
                        return -1
                    } else if (is_selected_b) {
                        return 1
                    }
                })
            },
            checkForScrollbar() {
                let scrollable_element = this.$refs.datatable?.$el?.children[0]
                if (!scrollable_element) {
                    this.scrollbar_is_present = false
                    return false
                }
                let is_present = scrollable_element.scrollHeight > scrollable_element.clientHeight
                this.$set(this, 'scrollbar_is_present' ,is_present)
                return true
            },
            scrollToTop() {
                this.$refs.datatable?.$el?.children[0]?.children[0]?.scrollIntoView(true)
            },
            scrollToBottom() {
                this.$refs.datatable?.$el?.children[0]?.children[0]?.scrollIntoView(false)
            },
        },
        watch: {
            value: {
                handler(newVal, oldVal) {
                    this.$set(this, 'selected', newVal)
                },
                deep: true,
            },
            show: {
                handler(newVal, oldVal) {
                    if (newVal===true) {
                        this.records_to_show = this.prep_data_for_ui()
                        setTimeout(()=>{
                            this.checkForScrollbar()
                            this.scrollToTop()
                        })
                    }
                    if (this.enable_search) {
                        setTimeout(() => {
                            let searchField = this.$refs?.searchField
                            if (searchField && (newVal === true)) {
                                searchField.focus()
                            }
                        })
                    }

                },
                deep: false,
                immediate: true,
            },
        },
    }
</script>

<style>
/* Selected Record */
.theme--light.v-data-table tbody tr.selected-record {
    background: #f5f5f5;
}

/* Column Headers */
th > span {
    font-weight: 700 !important;
    font-size: larger !important;
    text-transform: uppercase;
}

/* Border Underneath Table Header */
thead {
    border-bottom: 2px solid lightgrey !important;
}


</style>

